import React from 'react';
import * as R from 'ramda';
// components
import { TextComponent } from '../../components/text';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
//////////////////////////////////////////////////

const tableSettings = {
  minHeight: 340,
  cellFontSize: 13,
  titleFontSize: 14,
  titleRowHeight: 45,
  tableRowHeight: 40,
  allowEditBtn: true,
  allowSelectAll: true,
  checkBoxCellWidth: 90,
  searchableTitles: true,
  allowSelectItems: true,
  checkBoxCellJustifyContent: 'unset',
};

const SERVICE_PREFIX = 'fleetServiceEntity';
const QUICKBOOKS_EXPORT_TYPE_FLEET_SERVICE = 'FLEET_SERVICE';
const QUICKBOOKS_EXPORT_TYPE_FLEET_SERVICE_PAYABLE = 'FLEET_SERVICE_PAYABLE';
const QUICKBOOKS_EXPORT_TYPE_FLEET_SERVICE_EXPENSE_DISTRIBUTION_LINE = 'FLEET_SERVICE_EXPENSE_DISTRIBUTION_LINE';

const columnSettings = {
  [`${SERVICE_PREFIX}.${GC.FIELD_SERVICE_ID}`]: {
    width: 200,
    name: ['titles:service', 'titles:id'],
  },
  [`${SERVICE_PREFIX}.${GC.FIELD_SERVICE_DATE}`]: {
    width: 200,
    type: 'date',
    name: ['titles:service', 'titles:date'],
  },
  [`${SERVICE_PREFIX}.${GC.FIELD_STATUS}`]: {
    width: 200,
    type: 'enum',
    filterType: 'string',
    name: ['titles:service', 'titles:status'],
  },
  [`${SERVICE_PREFIX}.${GC.GRC.SERVICE_TYPE_DISPLAYED_VALUE}`]: {
    width: 200,
    name: ['titles:service', 'titles:type'],
  },
  [`${SERVICE_PREFIX}.${GC.FIELD_TOTAL}`]: {
    width: 200,
    type: 'totalAmount',
    filterType: 'number',
    name: ['titles:service', 'titles:total'],
    currencyKey: `${SERVICE_PREFIX}.${GC.FIELD_CURRENCY}`,
  },
  [`${SERVICE_PREFIX}.${GC.FIELD_CURRENCY}`]: {
    width: 200,
    options: GC.CURRENCY_OPTIONS,
    filterType: 'selectMultiple',
    name: ['titles:service', 'titles:currency'],
  },
  [`${SERVICE_PREFIX}.${GC.GRC.NORMALIZED_TOTAL_TOTAL}`]: {
    width: 250,
    type: 'totalAmount',
    filterType: 'number',
    currencyKey: `${SERVICE_PREFIX}.${GC.FIELD_CURRENCY}`,
    name: ['titles:service', 'titles:normalized-total'],
  },
  [`${SERVICE_PREFIX}.${GC.GRC.NORMALIZED_TOTAL_CURRENCY}`]: {
    width: 270,
    options: GC.CURRENCY_OPTIONS,
    filterType: 'selectMultiple',
    name: ['titles:service', 'titles:normalized-currency'],
  },
  [GC.FIELD_INVOICE_NUMBER]: {
    width: 150,
    name: 'titles:invoice-number',
  },
  [GC.GRC.STATUS_DISPLAYED_VALUE]: {
    width: 150,
    name: 'titles:invoice-status',
  },
  [GC.FIELD_INVOICE_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:invoice-date',
  },
  [GC.FIELD_TOTAL]: {
    width: 150,
    type: 'totalAmount',
    filterType: 'number',
    name: 'titles:total',
  },
  [GC.FIELD_CURRENCY]: {
    width: 150,
    name: 'titles:currency',
    options: GC.CURRENCY_OPTIONS,
    filterType: 'selectMultiple',
  },
  [GC.GRC.NORMALIZED_TOTAL_TOTAL]: {
    width: 200,
    type: 'totalAmount',
    filterType: 'number',
    name: 'titles:normalized-total',
    currencyKey: GC.GRC.NORMALIZED_TOTAL_CURRENCY,
  },
  [GC.GRC.NORMALIZED_TOTAL_CURRENCY]: {
    width: 200,
    options: GC.CURRENCY_OPTIONS,
    filterType: 'selectMultiple',
    name: 'titles:normalized-currency',
  },
  [GC.GRC.GL_CODE_DISPLAYED_VALUE]: {
    width: 150,
    name: 'titles:gl-code',
  },
  [GC.FIELD_PO_NUMBER]: {
    width: 150,
    name: 'titles:po-number',
  },
  [GC.FIELD_INVOICE_NET_DAYS]: {
    width: 150,
    name: 'titles:net-days',
  },
  [GC.FIELD_INVOICE_PAYMENT_DUE_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:payment-due-date',
  },
  [GC.FIELD_INVOICE_CHECK_NUMBER]: {
    width: 150,
    name: 'titles:check-number',
  },
  [GC.FIELD_INVOICE_CHECK_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:check-date',
  },
  [GC.FIELD_CHECK_AMOUNT]: {
    width: 150,
    name: 'titles:check-amount',
  },
  [GC.FIELD_INVOICE_DEPOSIT_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:deposit-date',
  },
  [GC.FIELD_INVOICE_ACH_PAYMENT_CONFIRMATION_NUMBER]: {
    width: 150,
    name: 'titles:ach-payment-confirmation',
  },
  [GC.FIELD_COMMENTS]: {
    width: 400,
    name: 'titles:comments',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_ID]: {
    width: 200,
    searchable: false,
    name: 'titles:integration-id',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_TRANSACTION_NUMBER]: {
    width: 200,
    searchable: false,
    name: 'titles:integration-transaction-number',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_STATUS]: {
    width: 200,
    searchable: false,
    name: 'titles:integration-status',
    customComponent: ({ data }) => {
      const status = R.pathOr(' ', [GC.GRC.ACCOUNTING_INTEGRATION_STATUS], data);
      const text = G.getWindowLocale(GC.statusLocaleMap[status], status);

      return (
        <TextComponent
          title={text}
          display='block'
          withEllipsis={true}
          maxWidth='calc(100% - 16px)'
        >
          {text}
        </TextComponent>
      );
    },
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_TYPE]: {
    width: 200,
    searchable: false,
    name: 'titles:integration-type',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_ERROR]: {
    width: 200,
    searchable: false,
    name: 'titles:integration-error',
  },
  [GC.GRC.ACCOUNTING_INTEGRATION_LAST_MODIFIED_BY]: {
    width: 200,
    searchable: false,
    name: 'titles:integrated-by',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_CREATED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:created-date',
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 200,
    name: 'titles:updated-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:updated-date',
  },
};

const FILTER_PARAMS = R.compose(
  R.values,
  R.mapObjIndexed(({ name, options, type = 'string', filterType }, value) => ({
    value,
    options,
    type: R.or(filterType, type),
    name: G.isArray(name) ? `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}` : G.getWindowLocale(name),
  })),
)(columnSettings);

export {
  FILTER_PARAMS,
  tableSettings,
  columnSettings,
  SERVICE_PREFIX,
};
